import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Users, 
  Loader2, 
  Sparkles, 
  Edit2, 
  Check, 
  X,
  User,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FaceGroupsPanel({ galleryId, isOwner = false, language = 'pl', onFilterByGroup }) {
  const [faceGroups, setFaceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzedAt, setAnalyzedAt] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    fetchFaceGroups();
  }, [galleryId]);

  const fetchFaceGroups = async () => {
    try {
      const response = await axios.get(`${API}/galleries/${galleryId}/face-groups`);
      setFaceGroups(response.data.face_groups || []);
      setAnalyzedAt(response.data.analyzed_at);
    } catch (error) {
      console.error('Failed to fetch face groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeFaces = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/analyze-faces`);
      setFaceGroups(response.data.face_groups || []);
      toast.success(language === 'pl' 
        ? `Przeanalizowano! Znaleziono ${response.data.face_groups?.length || 0} grup osób.`
        : `Analyzed! Found ${response.data.face_groups?.length || 0} person groups.`
      );
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd analizy' : 'Analysis error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpdateGroupName = async (groupId) => {
    try {
      await axios.put(`${API}/galleries/${galleryId}/face-groups/${groupId}`, {
        name: editName
      });
      setFaceGroups(groups => groups.map(g => 
        g.id === groupId ? { ...g, name: editName } : g
      ));
      setEditingGroup(null);
      toast.success(language === 'pl' ? 'Nazwa zaktualizowana' : 'Name updated');
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd aktualizacji' : 'Update error');
    }
  };

  const handleSelectGroup = (group) => {
    if (selectedGroup?.id === group.id) {
      setSelectedGroup(null);
      onFilterByGroup?.(null);
    } else {
      setSelectedGroup(group);
      onFilterByGroup?.(group.photo_ids);
    }
  };

  const texts = {
    pl: {
      title: 'Osoby na zdjęciach',
      analyze: 'Analizuj twarze (AI)',
      analyzing: 'Analizowanie...',
      noGroups: 'Brak danych o osobach',
      analyzeHint: 'Kliknij "Analizuj twarze" aby AI rozpoznało osoby',
      photos: 'zdjęć',
      showAll: 'Pokaż wszystkie',
      filterBy: 'Filtruj po osobie'
    },
    en: {
      title: 'People in photos',
      analyze: 'Analyze faces (AI)',
      analyzing: 'Analyzing...',
      noGroups: 'No person data',
      analyzeHint: 'Click "Analyze faces" for AI to recognize people',
      photos: 'photos',
      showAll: 'Show all',
      filterBy: 'Filter by person'
    }
  };

  const t = texts[language] || texts.pl;

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Users className="w-5 h-5" />
          {t.title}
        </h3>
        
        {isOwner && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleAnalyzeFaces}
            disabled={analyzing}
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t.analyzing}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {t.analyze}
              </>
            )}
          </Button>
        )}
      </div>

      {faceGroups.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{t.noGroups}</p>
          {isOwner && (
            <p className="text-xs mt-2">{t.analyzeHint}</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Show All Button */}
          {selectedGroup && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start"
              onClick={() => {
                setSelectedGroup(null);
                onFilterByGroup?.(null);
              }}
            >
              <X className="w-4 h-4 mr-2" />
              {t.showAll}
            </Button>
          )}
          
          {/* Face Groups */}
          {faceGroups.map((group) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedGroup?.id === group.id 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => handleSelectGroup(group)}
            >
              <div className="flex items-center gap-3">
                {/* Sample Photos */}
                <div className="flex -space-x-2">
                  {group.sample_photos?.slice(0, 3).map((url, idx) => (
                    <div 
                      key={idx}
                      className="w-10 h-10 rounded-full border-2 border-card overflow-hidden"
                    >
                      <img 
                        src={url} 
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                
                {/* Group Info */}
                <div className="flex-1 min-w-0">
                  {editingGroup === group.id ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateGroupName(group.id);
                          if (e.key === 'Escape') setEditingGroup(null);
                        }}
                      />
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8"
                        onClick={() => handleUpdateGroupName(group.id)}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8"
                        onClick={() => setEditingGroup(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm truncate">{group.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {group.photo_ids?.length || 0} {t.photos}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {isOwner && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingGroup(group.id);
                              setEditName(group.name);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        <ChevronRight className={`w-4 h-4 transition-transform ${
                          selectedGroup?.id === group.id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {analyzedAt && (
        <p className="text-xs text-muted-foreground mt-4 text-center">
          {language === 'pl' ? 'Ostatnia analiza' : 'Last analyzed'}: {new Date(analyzedAt).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}
